import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import LanguageSelector from "../components/LanguageSelector";
import api from "../lib/api";
import { toast } from "sonner";
import { DISPLAY_DOMAIN } from "../lib/brand";
import {
  ArrowRight,
  Shield,
  Lock,
  TrendingUp,
  Users,
  QrCode,
  CheckCircle2,
  BarChart3,
  Landmark,
  ShieldCheck,
  Calendar,
  Mail,
  Network,
  Handshake,
  Crown,
  ClipboardCheck
} from "lucide-react";
import TrustBadges from "../components/TrustBadges";
import CountUp from "../components/CountUp";
import RevealOnScroll from "../components/RevealOnScroll";
import NewsletterSignup from "../components/NewsletterSignup";
import LeadCapturePopup from "../components/LeadCapturePopup";
import OnboardingProgressBar from "../components/OnboardingProgressBar";
import SecurityAssurance from "../components/SecurityAssurance";
import LegalEntityDisclosure from "../components/LegalEntityDisclosure";
import { PioneerAvailabilityBadge } from "../components/PioneerAvailabilityBadge";
import PublicNav from "../components/PublicNav";

const LandingPage = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [showLeadPopup, setShowLeadPopup] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);
  const [referrerInfo, setReferrerInfo] = useState(null);

  useEffect(() => {
    // Seed admins on first load
    const seedAdmins = async () => {
      try {
        await api.post("/seed-admins");
      } catch (error) {
        // Non-fatal — admins are seeded on backend startup as well.
        // Log instead of swallowing so issues surface in dev.
        console.debug("LandingPage: /seed-admins skipped", error?.response?.status);
      }
    };
    seedAdmins();
    
    // Show lead capture popup after 30 seconds if not shown before
    const hasSeenPopup = localStorage.getItem("lead_popup_seen");
    if (!hasSeenPopup && !user) {
      const timer = setTimeout(() => {
        setShowLeadPopup(true);
        localStorage.setItem("lead_popup_seen", "true");
      }, 30000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const validateReferralCode = async () => {
    if (!referralCode.trim()) {
      toast.error("Please enter a referral code");
      return;
    }

    setValidatingCode(true);
    try {
      const response = await api.get(`/referrals/validate/${referralCode.trim()}`);
      setReferrerInfo(response.data);
      toast.success(`Valid code! Referred by ${response.data.referrer_name}`);
    } catch (error) {
      toast.error("Invalid referral code");
      setReferrerInfo(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleStartJourney = () => {
    if (referrerInfo) {
      navigate(`/register?ref=${referralCode}`);
    } else {
      navigate("/register");
    }
  };

  const features = [
    {
      icon: Network,
      title: "Private Member Network",
      description: "A private member portal connecting qualified individuals with approved regulated onboarding pathways. EchoSense does not manage funds, give advice, or approve accounts."
    },
    {
      icon: ShieldCheck,
      title: "Compliance-First Verification",
      description: "Identity, KYC/AML, and accredited / qualified investor checks are completed before any file is forwarded to a regulated partner."
    },
    {
      icon: Handshake,
      title: "Approved Regulated Partner Pathway",
      description: "After verification and written data-transfer consent, your file may be passed to the approved regulated onboarding partner who handles the rest of the process directly."
    },
    {
      icon: ClipboardCheck,
      title: "Transparent Status Tracking",
      description: "Track every step of your application — submission, KYC, accredited review, consent, partner transfer — through the Compliance Status portal."
    }
  ];

  const stats = [
    { value: 17,   suffix: "",   prefix: "",  label: "Onboarding Stages",  sub: "Granular status tracking" },
    { value: 4,    suffix: "",   prefix: "",  label: "Capital Tiers",      sub: "Standard → Sovereign" },
    { value: 99,   suffix: "",   prefix: "$", label: "Verification Fee",   sub: "One-time, all-inclusive" },
    { value: 100,  suffix: "%",  prefix: "",  label: "Compliance-First",   sub: "KYC · AML · Members-only" },
  ];

  const trustItems = [
    "ESTD 2026",
    "KYC · AML COMPLIANT",
    "PRIVATE CLUB MANDATE",
    "MEMBER-ONLY ACCESS",
    "MICROSOFT 365 SECURED",
    "REGULATED PARTNER PATHWAY",
    "PRIVATE CAPITAL MARKETS",
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Shared public nav (forge-20260609-003): sticky, in normal flow above
          the hero — replaces the page-local absolute bar. */}
      <PublicNav />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-obsidian">
        {/* Yacht hero — pinned to right half only (Zone 1 of two-zone brand strategy) */}
        <div
          className="hidden md:block absolute top-0 bottom-0 right-0 w-1/2 z-0 animate-ken-burns"
          data-testid="landing-hero-jet"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1540962351504-03099e0a754b?auto=format&fit=crop&w=2000&q=85')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Soft left→right fade INSIDE the yacht half so the seam never feels harsh */}
        <div
          className="hidden md:block absolute top-0 bottom-0 right-0 w-1/2 z-[1] pointer-events-none"
          style={{
            background:
              "linear-gradient(90deg, rgb(var(--brand-obsidian-rgb) / 0.96) 0%, rgb(var(--brand-obsidian-rgb) / 0.6) 18%, rgb(var(--brand-obsidian-rgb) / 0.15) 45%, rgb(var(--brand-obsidian-rgb) / 0) 80%, rgb(var(--brand-obsidian-rgb) / 0.25) 100%)",
          }}
        />
        {/* Sweeping light beam — cinematic "edge" across the dark side */}
        <div
          className="hidden md:block absolute top-0 bottom-0 left-0 w-2/3 z-[2] overflow-hidden pointer-events-none"
          aria-hidden="true"
        >
          <div
            className="absolute top-0 -left-1/4 h-full w-1/4 animate-light-beam"
            style={{
              background:
                "linear-gradient(90deg, transparent 0%, rgba(234,179,8,0.12) 50%, transparent 100%)",
              filter: "blur(40px)",
            }}
          />
        </div>
        {/* Mobile fallback: subtle dark with yacht as low-opacity bg */}
        <div
          className="md:hidden absolute inset-0 z-0 opacity-25 animate-ken-burns"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1540962351504-03099e0a754b?auto=format&fit=crop&w=1200&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        {/* Burgundy edge accent */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-burgundy/15 to-transparent z-[2] pointer-events-none"></div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-8 py-32 w-full">
          <div className="max-w-xl">
            {/* EXCLUSIVE NETWORK ACCESS eyebrow — bolder + brighter red so it pops
                against the dark navy hero. Was `#8B1538` (muted burgundy)
                which read as faded. Now rose-600 + extra-bold weight. */}
            <p className="uppercase tracking-[0.3em] text-sm mb-4 animate-hero-rise flex items-center gap-2 font-extrabold drop-shadow-md" style={{ color: "#DC2626" }}>
              <span
                className="inline-block w-1.5 h-1.5 rounded-full animate-hero-pulse"
                style={{ backgroundColor: "rgb(var(--brand-gold-rgb))" }}
              ></span>
              {t('landing.invite_badge')}
            </p>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-cream font-serif leading-tight animate-hero-rise hero-rise-delay-1">
              {t('landing.hero_title').split(' ').slice(0, -2).join(' ')}{" "}
              <span
                className="animate-gold-glow text-gold"
              >
                {t('landing.hero_title').split(' ').slice(-2).join(' ')}
              </span>
            </h1>
            <blockquote
              className="mt-7 pl-5 border-l-2 animate-hero-rise hero-rise-delay-2"
              style={{ borderColor: "rgb(var(--brand-gold-rgb) / 0.7)" }}
              data-testid="landing-tagline-quote"
            >
              <p className="text-lg md:text-xl text-platinum/90 italic font-serif leading-snug">
                The mission of EchoSense Capital Club is to connect high-net-worth individuals
                with opportunities that do not exist outside of institutions.
              </p>
            </blockquote>
            <p className="mt-8 text-xl text-platinum/85 leading-relaxed animate-hero-rise hero-rise-delay-3">
              {t('landing.hero_subtitle')}
            </p>

            {/* Cybersecurity reassurance — short, non-boastful, ships with hero */}
            <div className="mt-5 animate-hero-rise hero-rise-delay-3">
              <SecurityAssurance variant="homepage" />
            </div>

            {/* 4-step onboarding visual — answers "where do I begin?" instantly */}
            <div className="mt-7 max-w-2xl animate-hero-rise hero-rise-delay-4">
              <OnboardingProgressBar currentStep={1} variant="public" />
            </div>

            <div className="mt-8 flex flex-wrap gap-4 animate-hero-rise hero-rise-delay-4">
              <Button 
                size="lg"
                className="bg-gold hover:bg-gold-light text-navy-dark shadow-sm font-medium px-8"
                onClick={() => navigate("/verification")}
                data-testid="hero-cta-button"
              >
                Start Investor Qualification
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-platinum/30 text-platinum hover:bg-platinum/10"
                onClick={() => document.getElementById("vip-club").scrollIntoView({ behavior: "smooth" })}
                data-testid="hero-cta-secondary"
              >
                {t('landing.cta_secondary')}
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-platinum/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-platinum/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Trust Marquee — slowly scrolling compliance strip (cinematic touch) */}
      <section
        className="py-5 bg-surface-highlight border-y border-platinum/10 overflow-hidden relative"
        data-testid="trust-marquee"
        aria-label="Compliance trust strip"
      >
        {/* Edge fade masks */}
        <div className="absolute left-0 top-0 bottom-0 w-32 z-10 pointer-events-none"
             style={{ background: "linear-gradient(90deg, rgb(var(--brand-surface-highlight-rgb)) 0%, transparent 100%)" }} />
        <div className="absolute right-0 top-0 bottom-0 w-32 z-10 pointer-events-none"
             style={{ background: "linear-gradient(-90deg, rgb(var(--brand-surface-highlight-rgb)) 0%, transparent 100%)" }} />
        <div className="flex w-max animate-marquee">
          {[...trustItems, ...trustItems].map((item, idx) => (
            <div key={`${item}-${idx}`} className="flex items-center gap-8 px-6">
              <span className="text-platinum/65 text-xs tracking-[0.35em] uppercase whitespace-nowrap">
                {item}
              </span>
              <span className="text-platinum/25 text-lg leading-none" aria-hidden="true">◆</span>
            </div>
          ))}
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-8 bg-surface-highlight border-b border-platinum/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <RevealOnScroll
                key={stat.label}
                delay={index * 120}
                className="text-center"
              >
                <p
                  className="text-5xl md:text-6xl font-serif font-semibold tabular-nums text-gold"
                >
                  <CountUp
                    to={stat.value}
                    prefix={stat.prefix}
                    suffix={stat.suffix}
                    duration={1600}
                    data-testid={`stat-counter-${index}`}
                  />
                </p>
                <p className="text-platinum text-sm mt-3 uppercase tracking-widest font-medium">
                  {stat.label}
                </p>
                <p className="text-platinum/50 text-xs mt-1">{stat.sub}</p>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Pioneer Wall teaser — social proof for the VIP Capital Club */}
      <PioneerWallTeaser />

      {/* Features Section */}
      <section className="py-24 px-8 bg-obsidian">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">How It Works</p>
            <h2 className="text-4xl md:text-5xl font-semibold text-platinum font-serif">
              Verify. <span className="text-gold">Consent.</span> Onboard.
            </h2>
            <p className="mt-4 text-lg text-platinum/60 max-w-2xl mx-auto">
              EchoSense Capital Club is a private member, introducer, and verification
              portal. We do not receive client funds, provide investment advice, or approve
              investor participation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <RevealOnScroll key={feature.title} delay={index * 110}>
                <Card
                  className="bg-surface border-platinum/15 card-hover h-full"
                >
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-sm bg-burgundy/20 flex items-center justify-center mb-4">
                      <feature.icon className="text-burgundy" size={24} />
                    </div>
                    <h3 className="text-lg font-semibold text-platinum mb-2 font-serif">{feature.title}</h3>
                    <p className="text-platinum/60 text-sm leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership with EchoSense Capital Club */}
      <section className="py-24 px-8 bg-surface-highlight/60">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">Onboarding Pathway</p>
              <h2 className="text-4xl md:text-5xl font-semibold text-platinum font-serif mb-6">
                Approved <span className="text-gold">Regulated Partner</span> Onboarding Pathway
              </h2>
              <p className="text-platinum/70 mb-6 text-lg leading-relaxed">
                After completing KYC/AML, accredited or qualified investor verification, and a
                written data-transfer consent, your file may be forwarded to the approved
                regulated onboarding partner. The partner — not EchoSense — handles account
                approval, funding instructions, reporting, and tax documentation directly with you.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-3 text-platinum/80">
                  <CheckCircle2 className="text-emerald" size={20} />
                  <span>Identity, KYC and AML verification</span>
                </div>
                <div className="flex items-center gap-3 text-platinum/80">
                  <CheckCircle2 className="text-emerald" size={20} />
                  <span>Accredited / qualified investor review</span>
                </div>
                <div className="flex items-center gap-3 text-platinum/80">
                  <CheckCircle2 className="text-emerald" size={20} />
                  <span>Written data-transfer consent</span>
                </div>
                <div className="flex items-center gap-3 text-platinum/80">
                  <CheckCircle2 className="text-emerald" size={20} />
                  <span>Transfer to regulated partner pathway</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-br from-burgundy/20 to-transparent rounded-lg"></div>
              <Card className="bg-surface border-platinum/15 relative">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <Landmark className="text-platinum" size={32} />
                    <div>
                      <h3 className="text-platinum font-serif text-xl font-semibold">Regulated Partner Pathway</h3>
                      <p className="text-platinum/50 text-sm">Identity confirmed at approval stage</p>
                    </div>
                  </div>
                  <div className="space-y-4 text-platinum/70 text-sm">
                    <p>
                      EchoSense Capital Club is an introducer, member and verification portal.
                      We do not receive client funds, manage trading accounts, provide investment
                      advice, or guarantee any result.
                    </p>
                    <p>
                      Final account approval, funding instructions, tax reporting and account
                      access may be provided directly by the applicable regulated partner, fund,
                      adviser, platform, administrator, or other approved third party.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* VIP Capital Club — Member-Only Access (compliant) */}
      <section id="vip-club" className="py-24 px-8 bg-obsidian" data-testid="vip-club-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-burgundy uppercase tracking-[0.2em] text-sm mb-4">EchoSense Capital Club</p>
            <h2 className="text-4xl md:text-5xl font-semibold text-platinum font-serif">
              A <span className="text-gold">Private Door</span> Most People Never See
            </h2>
            <p className="mt-5 text-lg text-platinum/60 max-w-3xl mx-auto leading-relaxed">
              EchoSense Capital Club is an invitation-only membership portal. Once you are
              verified inside the Club, you gain entry to a curated pathway that the general
              public cannot access — including handoff to the approved regulated onboarding
              partner and the members-only Deal Room that sits behind it.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* 1 */}
            <Card className="bg-surface border-platinum/15 hover:border-burgundy/40 transition-all" data-testid="vip-club-pillar-1">
              <CardContent className="p-7">
                <div className="w-12 h-12 rounded-sm bg-burgundy/15 flex items-center justify-center mb-5">
                  <ShieldCheck className="text-burgundy" size={22} />
                </div>
                <h3 className="text-cream font-serif text-xl mb-2">Private Verification Lane</h3>
                <p className="text-platinum/70 text-sm leading-relaxed">
                  KYC, AML, and accredited / qualified investor review are completed inside the
                  Club — not in public forms. Members reach <strong>Accredited / KYC Approved</strong>
                  status on a clean, compliance-first track.
                </p>
              </CardContent>
            </Card>

            {/* 2 */}
            <Card className="bg-surface border-platinum/15 hover:border-burgundy/40 transition-all" data-testid="vip-club-pillar-2">
              <CardContent className="p-7">
                <div className="w-12 h-12 rounded-sm bg-burgundy/15 flex items-center justify-center mb-5">
                  <Landmark className="text-burgundy" size={22} />
                </div>
                <h3 className="text-cream font-serif text-xl mb-2">Regulated Partner Handoff</h3>
                <p className="text-platinum/70 text-sm leading-relaxed">
                  Once verified and consented, your file is forwarded to the
                  <strong> Approved Regulated Partner Onboarding Pathway</strong>. The regulated
                  partner — not EchoSense — handles account approval, funding instructions, and
                  reporting directly with you. Non-members do not receive this introduction.
                </p>
              </CardContent>
            </Card>

            {/* 3 — Introducer Relationship Network pillar removed for the time being
                (STARSEED-CAPMARKETS-051); a Club Bylaws pillar takes a freed slot once
                the bylaws arrive (STARSEED-CAPMARKETS-062). */}
            <Card className="bg-surface border-platinum/15 hover:border-burgundy/40 transition-all" data-testid="vip-club-pillar-4">
              <CardContent className="p-7">
                <div className="w-12 h-12 rounded-sm bg-burgundy/15 flex items-center justify-center mb-5">
                  <ClipboardCheck className="text-burgundy" size={22} />
                </div>
                <h3 className="text-cream font-serif text-xl mb-2">Live Compliance Status</h3>
                <p className="text-platinum/70 text-sm leading-relaxed">
                  Each member sees real-time progress: intake submitted, KYC, accredited,
                  data-transfer consent, partner transfer, partner onboarding. Sixteen
                  distinct statuses keep every applicant informed without phone tag.
                </p>
              </CardContent>
            </Card>

            {/* 5 */}
            <Card className="bg-surface border-platinum/15 hover:border-burgundy/40 transition-all" data-testid="vip-club-pillar-5">
              <CardContent className="p-7">
                <div className="w-12 h-12 rounded-sm bg-burgundy/15 flex items-center justify-center mb-5">
                  <Lock className="text-burgundy" size={22} />
                </div>
                <h3 className="text-cream font-serif text-xl mb-2">Deal Room</h3>
                <p className="text-platinum/70 text-sm leading-relaxed">
                  Members who have completed onboarding through the regulated partner unlock the
                  private <strong>Deal Room</strong> — a tiered, members-only viewing room
                  that the general public cannot see. Access is gated on verified status and is
                  never visible during the initial onboarding flow.
                </p>
              </CardContent>
            </Card>

            {/* 6 — Curated Relationship Layer pillar removed (STARSEED-CAPMARKETS-051):
                introducer team must not be highlighted on the main page. */}
          </div>

          {/* Membership cost + how to enter */}
          <div className="mt-14 grid lg:grid-cols-2 gap-8 items-stretch">
            <Card className="bg-burgundy/10 border-burgundy/30">
              <CardContent className="p-7">
                <p className="text-[10px] uppercase tracking-[0.3em] text-burgundy font-semibold mb-3">Membership</p>
                <h3 className="text-platinum font-serif text-2xl mb-3 font-semibold">How to Enter the Club</h3>
                <ol className="space-y-3 text-platinum/80 text-sm leading-relaxed">
                  <li className="flex gap-3">
                    <span className="font-mono text-burgundy text-xs mt-0.5">01</span>
                    <span>Complete your private intake — basic profile, residency, intended role.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-burgundy text-xs mt-0.5">02</span>
                    <span>You receive your member username and password instantly.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-burgundy text-xs mt-0.5">03</span>
                    <span>Log in and complete KYC + Accredited / Qualified Investor review.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="font-mono text-burgundy text-xs mt-0.5">04</span>
                    <span>On approval, your file is forwarded to the regulated partner pathway.</span>
                  </li>
                </ol>
                <Button
                  size="lg"
                  className="mt-6 bg-gold hover:bg-gold-light text-navy-dark shadow-sm font-medium px-6"
                  onClick={() => navigate("/verification")}
                  data-testid="vip-club-cta"
                >
                  Start Investor Qualification
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-surface border-platinum/15">
              <CardContent className="p-7">
                <p className="text-[10px] uppercase tracking-[0.3em] text-platinum/60 font-semibold mb-3">Important</p>
                <h3 className="text-platinum font-serif text-2xl mb-3 font-semibold">What Membership Is — and Is Not</h3>
                <p className="text-platinum/70 text-sm leading-relaxed mb-3">
                  EchoSense Capital Club is a <strong>club</strong>. Membership grants
                  <strong> access</strong> to the qualification portal, the live compliance
                  tracker, and — once fully onboarded through the regulated partner — the
                  members-only Deal Room.
                </p>
                <p className="text-platinum/60 text-xs leading-relaxed">
                  EchoSense screens and verifies people for club eligibility, but the issuer or
                  regulated counterparty still makes its own required eligibility, KYC/AML,
                  suitability, account-opening, and funding decisions. EchoSense does not receive
                  client funds, provide investment advice, manage trading accounts, issue tax
                  forms, or approve investor participation.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Affluent Lifestyle — luxury hero image. Replaces the old public
          Network Builder section, which now lives in /introducer-program
          and unlocks only after Accredited / KYC Approved milestone. */}
      <section id="referral-desk" className="relative py-32 px-8 overflow-hidden" data-testid="luxury-hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "linear-gradient(to right, rgb(var(--brand-obsidian-rgb) / 0.95) 0%, rgb(var(--brand-obsidian-rgb) / 0.72) 55%, rgb(var(--brand-obsidian-rgb) / 0.45) 100%), url('https://images.unsplash.com/photo-1474302770737-173ee21bab63?auto=format&fit=crop&w=2000&q=80')",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 items-center">
          <div className="space-y-6">
            <p className="text-gold uppercase tracking-[0.3em] text-xs font-semibold">
              Members Only · By Invitation
            </p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold text-cream leading-tight">
              A Door Reserved <br className="hidden md:block" />
              <span className="text-gold">for the Few</span>
            </h2>
            <p className="text-lg text-platinum/80 leading-relaxed max-w-xl">
              EchoSense Capital Club serves a curated circle of affluent, accredited investors —
              individuals who value documentation, discretion, and a clean compliance-first pathway
              to the approved regulated partner. The Network Builder program is reserved for fully
              verified members inside the portal.
            </p>
            <div className="flex flex-wrap gap-6 pt-2">
              <div>
                <p className="text-3xl font-serif text-gold font-semibold">Curated</p>
                <p className="text-xs text-platinum/60 uppercase tracking-widest mt-1">Membership</p>
              </div>
              <div>
                <p className="text-3xl font-serif text-gold font-semibold">Verified</p>
                <p className="text-xs text-platinum/60 uppercase tracking-widest mt-1">Accredited Only</p>
              </div>
              <div>
                <p className="text-3xl font-serif text-gold font-semibold">Discreet</p>
                <p className="text-xs text-platinum/60 uppercase tracking-widest mt-1">Written Disclosures</p>
              </div>
            </div>
          </div>

          {/* Glass-morphism CTA card with Join-Through-Introducer flow preserved */}
          <Card className="bg-surface/80 backdrop-blur-xl border-platinum/15 ring-1 ring-gold/10 shadow-xl">
            <CardHeader>
              <CardTitle className="text-cream font-serif flex items-center gap-3 font-semibold">
                <Users className="text-gold" size={24} />
                Join Through an Introducer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Input
                  placeholder="Enter VC# (e.g., VC-ABC12345 or EQS-CORP-2026)"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  className="bg-surface border-platinum/20 text-cream uppercase placeholder:text-platinum/50"
                  data-testid="referral-code-input"
                />
                <Button
                  onClick={validateReferralCode}
                  disabled={validatingCode}
                  className="w-full bg-amber-500/90 hover:bg-amber-400 text-slate-900 font-semibold"
                  data-testid="validate-code-button"
                >
                  {validatingCode ? "Validating..." : "Validate Code"}
                </Button>
              </div>

              {referrerInfo && (
                <div className="p-4 rounded bg-emerald/10 border border-emerald/30">
                  <div className="flex items-center gap-2 text-emerald">
                    <CheckCircle2 size={20} />
                    <span className="font-semibold">Valid VC#</span>
                  </div>
                  <p className="text-platinum/70 mt-2">Introduced by: <span className="text-cream font-semibold">{referrerInfo.referrer_name}</span></p>
                </div>
              )}

              <div className="border-t border-platinum/15 pt-6">
                <Button
                  onClick={handleStartJourney}
                  className="w-full bg-gold hover:bg-gold-light text-navy-dark font-semibold"
                  size="lg"
                  data-testid="start-journey-button"
                >
                  Begin Your Journey
                  <ArrowRight className="ml-2" size={20} />
                </Button>
                <p className="text-center text-platinum/50 text-sm mt-4">
                  $999 registration fee · $99/month membership
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* STARSEED-CAPMARKETS-090 item 2: the "What Our Members Say" section was
          removed along with the Testimonials component it rendered. Its three
          quotes — "Michael R. · Texas", "Sarah L. · New York", "David K. ·
          California", each with a five-star rating — were invented placeholder
          copy presented as real member endorsements. That was already an FTC
          endorsement-guides problem on its own; it became untenable next to the
          four real, named executives 088 put on the About page.

          Genuine social proof on this page is the live Pioneer Wall teaser
          above, which reads real claimed seats from /pioneers/wall — so the page
          does not lose its social-proof beat by dropping this section.

          Restoring a testimonials section needs real member quotes collected
          with written permission, not a repopulated placeholder array. */}

      {/* Trust Badges Section */}
      <section className="py-16 px-8 bg-surface-highlight/60 border-y border-platinum/10">
        <div className="max-w-5xl mx-auto">
          <TrustBadges />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-8 bg-surface-highlight/60 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-burgundy/10 via-transparent to-burgundy/10"></div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-platinum font-serif mb-6">
            Begin Your Investor Qualification
          </h2>
          <p className="text-xl text-platinum/60 leading-relaxed mb-8">
            Submit your intake, complete verification, and — once approved — be forwarded to the
            regulated partner onboarding pathway.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button 
              size="lg"
              className="bg-gold hover:bg-gold-light text-navy-dark shadow-sm font-medium px-8"
              onClick={() => navigate("/verification")}
              data-testid="cta-start-qualification"
            >
              Start Investor Qualification
              <ArrowRight className="ml-2" size={20} />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-platinum/30 text-platinum hover:bg-platinum/10 px-8"
              onClick={() => navigate("/consultation")}
            >
              <Calendar className="mr-2" size={20} />
              Schedule a Call
            </Button>
          </div>
        </div>
      </section>

      {/* Compliance Notice */}
      <section className="py-10 px-8 bg-obsidian border-t border-platinum/10" data-testid="public-compliance-notice">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start gap-4">
            <Shield className="text-burgundy mt-1 shrink-0" size={20} />
            <p className="text-xs leading-relaxed text-platinum/60">
              <span className="font-semibold text-platinum/80">Compliance Notice.</span>{" "}
              EchoSense Capital Club provides a private member, introducer, and verification
              portal. EchoSense does not receive client funds, provide investment advice, guarantee
              returns, manage trading accounts, issue tax forms, or approve investor participation.
              Final account approval, funding instructions, tax reporting, and account access may
              be provided directly by the applicable regulated partner, fund, adviser, platform,
              administrator, or other approved third party.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-8 bg-obsidian border-t border-platinum/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-4 mb-4">
                <img src="/logo.png" alt="EchoSense Capital Club" className="w-14 h-14 object-contain" />
                <div>
                  <span className="text-xl font-semibold text-platinum font-serif">EchoSense Capital Club</span>
                  <span className="text-platinum/40 text-sm block">{DISPLAY_DOMAIN}</span>
                </div>
              </div>
              <p className="text-platinum/50 text-sm">
                {language === 'en'
                  ? 'Private membership and compliance tracking platform.'
                  : 'Plataforma privada de membresía y seguimiento de cumplimiento.'
                }
              </p>
            </div>
            <div>
              <h4 className="text-platinum font-medium mb-4">{language === 'en' ? 'Quick Links' : 'Enlaces Rápidos'}</h4>
              <div className="space-y-2">
                <Link to="/about" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.about')}</Link>
                <Link to="/consultation" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.consultation')}</Link>
                <Link to="/contact" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.contact')}</Link>
                <Link to="/login" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.login')}</Link>
                <Link to="/register" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">{t('nav.register')}</Link>
              </div>
            </div>
            <div>
              <h4 className="text-platinum font-medium mb-4">{language === 'en' ? 'Contact' : 'Contacto'}</h4>
              <div className="space-y-2">
                <a href="mailto:admin@echosensecapital.com" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">
                  admin@echosensecapital.com
                </a>
                <a href="tel:+17025180208" className="block text-platinum/50 hover:text-platinum transition-colors text-sm">
                  (702) 518-0208
                </a>
              </div>
            </div>
            <div>
              <h4 className="text-platinum font-medium mb-4">{language === 'en' ? 'Stay Updated' : 'Mantente Actualizado'}</h4>
              <p className="text-platinum/50 text-sm mb-4">
                {language === 'en' 
                  ? 'Get updates from the Club.' 
                  : 'Recibe actualizaciones del Club.'
                }
              </p>
              <NewsletterSignup variant="inline" />
            </div>
          </div>
          
          <div className="pt-8 border-t border-platinum/10">
            {/* Cybersecurity assurance — sits above copyright line */}
            <div className="mb-4 text-center">
              <SecurityAssurance variant="footer" />
            </div>

            {/* Legal entity disclosure — required by KYC providers (SumSub) */}
            <div className="mb-4">
              <LegalEntityDisclosure variant="footer" />
            </div>

            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex items-center gap-4">
                <p className="text-platinum/40 text-xs">
                  © {new Date().getFullYear()} EchoSense Capital Club. {language === 'en' ? 'All rights reserved.' : 'Todos los derechos reservados.'}
                </p>
                <Link to="/disclaimer" className="text-platinum/40 hover:text-platinum text-xs underline-offset-2 hover:underline" data-testid="footer-disclaimer">
                  {language === 'en' ? 'Disclaimer' : 'Aviso legal'}
                </Link>
                <Link to="/privacy" className="text-platinum/40 hover:text-platinum text-xs underline-offset-2 hover:underline" data-testid="footer-privacy">
                  {language === 'en' ? 'Privacy' : 'Privacidad'}
                </Link>
                <Link to="/sms-terms" className="text-platinum/40 hover:text-platinum text-xs underline-offset-2 hover:underline" data-testid="footer-sms-terms">
                  {language === 'en' ? 'SMS Terms' : 'Términos SMS'}
                </Link>
                <LanguageSelector variant="minimal" />
              </div>
              <p className="text-platinum/30 text-xs text-center max-w-2xl">
                <strong>{language === 'en' ? 'Compliance Statement:' : 'Declaración de Cumplimiento:'}</strong> {t('about.conclusion_content')}
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Lead Capture Popup */}
      {showLeadPopup && <LeadCapturePopup onClose={() => setShowLeadPopup(false)} />}
    </div>
  );
};

/**
 * Pioneer Wall teaser strip — pulls the live counter so we drive FOMO
 * without forcing every visitor to navigate to /pioneers.
 */
const PioneerWallTeaser = () => {
  const [stats, setStats] = useState({ claimed: 0, remaining: 100, total_seats: 100, pioneers: [] });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/pioneers/wall");
        if (!cancelled) setStats(r.data || {});
      } catch (err) {
        console.error("PioneerWallTeaser: failed to load /pioneers/wall", err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const previewCount = 8;
  const preview = (stats.pioneers || []).slice(0, previewCount);

  return (
    <section
      className="py-20 px-8 bg-gradient-to-b from-obsidian via-surface-highlight to-obsidian border-y border-gold/20 relative overflow-hidden"
      data-testid="pioneer-wall-teaser"
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 80% 50%, rgba(234,179,8,0.08) 0%, transparent 55%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold text-[10px] uppercase tracking-[0.3em] mb-5">
            <Crown size={11} /> Wall of Champions
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-semibold text-platinum leading-tight">
            The Founding{" "}
            <span className="text-gold">100 Pioneers</span>
          </h2>
          <p className="mt-5 text-lg text-platinum/70 leading-relaxed">
            Members №001 through №100 are sealed for life — recognized by
            number, locked into Pioneer pricing, and granted priority access
            to the Deal Room.
          </p>

          <div className="mt-7 max-w-md" data-testid="pioneer-teaser-status">
            <PioneerAvailabilityBadge claimed={stats.claimed} compact />
          </div>

          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/pioneers" data-testid="landing-view-wall">
              <Button className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold">
                View The Wall of Champions
                <ArrowRight size={14} className="ml-2" />
              </Button>
            </Link>
            <Link to="/register" data-testid="landing-claim-pioneer">
              <Button
                variant="outline"
                className="border-platinum/30 text-platinum hover:bg-platinum/10 bg-transparent"
              >
                Claim Your Pioneer Seat
              </Button>
            </Link>
          </div>
        </div>

        {/* Live preview tiles */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="pioneer-preview-grid">
          {preview.length === 0 ? (
            <div className="col-span-full rounded-lg border border-gold/30 bg-gold/[0.05] p-6 text-center">
              <Crown className="mx-auto text-gold mb-2" size={24} />
              <p className="text-sm text-platinum/80 font-semibold">
                Be Pioneer №001
              </p>
              <p className="text-xs text-platinum/50 mt-1">The wall awaits its first member.</p>
            </div>
          ) : (
            preview.map((p) => (
              <div
                key={`teaser-${p.pioneer_number}`}
                className="rounded-lg border border-gold/20 bg-gradient-to-b from-gold/[0.07] to-transparent p-3"
              >
                <p className="text-[9px] uppercase tracking-[0.3em] text-gold/70 mb-0.5">Pioneer</p>
                <p className="text-2xl font-serif font-semibold tabular-nums leading-none text-gold">
                  №{String(p.pioneer_number || 0).padStart(3, "0")}
                </p>
                <p className="text-xs text-platinum/85 mt-2 truncate">{p.display_name}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
};

export default LandingPage;
